import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useContext } from "react";
import UserContext from "../../services/user/user";
import EPieChart from "./epiechart";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { FormattedMessage } from "react-intl";

export default function ECard({
  title,
  value,
  percent,
  chartObj,
  status,
  action,
  gaugeStyle,
  neutralSurface,
  variant,
  "data-testid": dataTestId,
  cardHeaderTestId,
  cardValueTestId,
  cardPercentTestId,
  cardChartTestId
}) {
  const { formatNumber } = useContext(UserContext);

  const theme = useTheme();
  const isTabletLandscape = useMediaQuery(theme.breakpoints.down(1371));

  // "validTaxRate" variant applies the dedicated header/body/footer spec
  // (SPOT-1171) for the "valid tax rates" cards only; all other cards are
  // unaffected.
  const isValidTaxRate = variant === "validTaxRate";

  return (
    <Card
      className={`${chartObj && "percentage-card"} card ${
        isValidTaxRate ? "valid-tax-rate" : ""
      } ${
        chartObj &&
        (Math.abs(chartObj?.value) === 100
          ? "success"
          : Math.abs(chartObj?.value) > 49
          ? "warning"
          : "error")
      }
      ${neutralSurface ? "neutral-surface" : status === "noData" ? "no-data" : status}
      `}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        padding: isValidTaxRate ? 0 : "8px 12px"
      }}
      elevation={0}
      data-testid={dataTestId}
    >
      <CardHeader
        style={
          isValidTaxRate
            ? { padding: "10px 16px", height: "48px", boxSizing: "border-box" }
            : { padding: 0 }
        }
        title={
          <Box
            className={`card-title ${status === "noData" && "no-data"}`}
            style={
              isValidTaxRate
                ? {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: isTabletLandscape ? "16px" : "20px",
                    lineHeight: "1.4",
                    color: "var(--font-strong-default)"
                  }
                : {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: isTabletLandscape ? "16px" : "20px",
                    lineHeight: "28px",
                    marginBottom: "7px"
                  }
            }
            data-testid={cardHeaderTestId}
          >
            <div>{title}</div>
            <div style={{ display: "flex", alignItems: "center" }}>{action}</div>
          </Box>
        }
        titleTypographyProps={{
          fontSize: 20,
          lineHeight: "28px",
          fontWeight: 600
        }}
      />
      <CardContent
        sx={{ height: "100%" }}
        style={
          isValidTaxRate
            ? { padding: "0 16px", height: "116px", boxSizing: "border-box" }
            : { padding: 0 }
        }
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: isValidTaxRate ? "space-between" : undefined,
            alignItems: isValidTaxRate ? "center" : undefined
          }}
        >
          <Box
            sx={{
              width: isValidTaxRate ? "auto" : chartObj ? "70%" : "100%",
              flex: isValidTaxRate ? 1 : undefined,
              minWidth: 0,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Typography
              className={`card-value ${status === "noData" && "no-data"}`}
              sx={{
                fontSize: isTabletLandscape ? "24px" : "28px",
                fontWeight: 600,
                lineHeight: 1.1,
                mt: "auto",
                mb: "auto",
                overflowWrap: "anywhere"
              }}
              data-testid={cardValueTestId}
            >
              {typeof value !== "undefined" && value !== null ? (
                value
              ) : (
                <FormattedMessage id="noData" />
              )}
            </Typography>
            {!isValidTaxRate && (
              <Typography
                className={`percent ${
                  status === "noData" ? "no-data" : status
                }`}
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  marginTop: "7px"
                }}
                data-testid={cardPercentTestId}
              >
                {typeof value !== "undefined" && value !== null ? percent : ""}
              </Typography>
            )}
          </Box>
          {chartObj ? (
            <Box
              sx={{
                width: isValidTaxRate ? "120px" : "30%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: isValidTaxRate ? "flex-end" : "center",
                alignSelf: "flex-center",
                mb: "auto",
                mt: "auto"
              }}
              className={`ecard-chart ${status}`}
              data-testid={cardChartTestId}
            >
              <EPieChart
                innerSize="90%"
                data={[
                  {
                    y: Math.abs(chartObj.value),
                    color:
                      gaugeStyle === "navy"
                        ? "var(--new-primary)"
                        : Math.abs(chartObj.value) === 100
                        ? "var(--font-strong-success)"
                        : Math.abs(chartObj.value) > 49
                        ? "var(--font-strong-warning)"
                        : "var(--font-heavy-error)",
                    dataLabels: {
                      enabled: false
                    }
                  },
                  {
                    y: Math.abs(100 - chartObj.value),
                    color: "var(--new-disabled)",
                    dataLabels: {
                      enabled: false
                    }
                  }
                ]}
                optionsOver={{
                  chart: {
                    type: "pie",
                    width: 120,
                    height: 120
                  },
                  title: {
                    text: `<text class="card-percentage-value">${
                      chartObj.value !== ""
                        ? formatNumber(chartObj.value, 2) + "%"
                        : "-"
                    }</text>`,
                    verticalAlign: "middle",
                    align: "center",
                    style: {
                      fontSize: "24px",
                      fontWeight: "500",
                      fontFamily: "Inter"
                    }
                  },
                  tooltip: {
                    enabled: false
                  },
                  legend: {
                    enabled: false
                  },
                  plotOptions: {
                    pie: {
                      size: 116,
                      borderWidth: 0
                    }
                  }
                }}
              />
            </Box>
          ) : (
            ""
          )}
        </Box>
      </CardContent>
      {isValidTaxRate && (
        <Box
          sx={{
            height: "56px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "stretch",
            gap: "12px",
            padding: "16px"
          }}
        >
          <Typography
            className={`percent ${status === "noData" ? "no-data" : status}`}
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1.5,
              textAlign: "left",
              color: "var(--font-strong-default)"
            }}
            data-testid={cardPercentTestId}
          >
            {typeof value !== "undefined" && value !== null ? percent : ""}
          </Typography>
        </Box>
      )}
    </Card>
  );
}
